import { createSelector } from '@reduxjs/toolkit';
import _, { uniq } from 'lodash';

import { isCurrentPageSel } from '@location';
import { selectedUnitSel } from '@modules/councilUnits/duck/selectors';
import {
  allSubUnitsSel,
  allUnitsSubunitsSel,
  allUnitsWithColorsSel,
  calendarRecordsSel,
} from '@modules/events/duck/selectors';
import { getRelatedChildrenSel } from '@modules/profile/duck/selectors';
import { userDataSel, userIdSel } from '@modules/user/duck/selectors';
import { PositionId } from '@shared/constants';
import { organizationPositionsSel } from '@shared/duck/selectors/general.selectors';
import isValueTruthy from '@shared/utils/isValueTruthy';
import { unitInfoSel } from '@unit/duck/selectorsTyped';
import { allowUserChangeRsvp, simpleUnitFormat, sorters } from '@utils';

import { calendarViews, moduleName } from '../constants';
import { sortFoundSubUnits } from '../utils';
import {
  ROUTE_CALENDAR,
  ROUTE_CALENDAR_ACTIVITY_DETAILS,
  ROUTE_CALENDAR_CREATE_ACTIVITY,
  ROUTE_CALENDAR_EDIT_ACTIVITY,
  ROUTE_CALENDAR_EVENT_DETAILS,
} from './actions';

const moduleSel = state => state[moduleName];

export const isCalendarPageRouteSel = state =>
  isCurrentPageSel(
    state,
    ...[
      ROUTE_CALENDAR_EVENT_DETAILS,
      ROUTE_CALENDAR_ACTIVITY_DETAILS,
      ROUTE_CALENDAR_CREATE_ACTIVITY,
      ROUTE_CALENDAR_EDIT_ACTIVITY,
    ],
  );

export const selectedDateSel = state => moduleSel(state).selectedDate;
export const miniCalendarSelectedDateSel = state =>
  moduleSel(state).miniCalendarSelectedDate || moduleSel(state).selectedDate;
export const selectedMonthSel = state => moduleSel(state).selectedMonth;
export const selectedRangeDateSel = state => moduleSel(state).selectedRangeDate;
export const calendarViewSel = state => moduleSel(state).calendarView;

export const addEventDatesSel = state => moduleSel(state).addEventDates;
export const addEventPopoverVisibleSel = state =>
  moduleSel(state).addEventPopoverVisible;

export const activitiesSel = state => moduleSel(state).activities;

export const eventsSel = state => moduleSel(state).events;

export const personalActivitiesSel = state =>
  moduleSel(state).personalActivities;

export const calendarRangeSel = state => moduleSel(state).selectedCalendarRange;

export const activityTypeFiltersSel = state =>
  moduleSel(state).activityTypeFilters;

export const isEventsLoadingSel = state => moduleSel(state).isEventsLoading;

export const isFeedEventsLoadingSel = state =>
  moduleSel(state).isFeedEventsLoading;

export const isPersonalActivitiesLoadingSel = state =>
  moduleSel(state).isPersonalActivitiesLoading;

export const calendarsToHideSel = state => moduleSel(state).calendarsToHide;
export const searchEventNameSel = state => moduleSel(state).searchEventName;
export const eventsSelectFiltersSel = state =>
  moduleSel(state).eventsSelectFilters;

export const eventsWithFormatedUnits = createSelector(
  eventsSel,
  organizationPositionsSel,
  (events, positions) =>
    events.map(event => {
      const {
        resource: { units = [], invitedUsers = [], userId: eventUserId },
      } = event;
      const formattedUnits = units.map(unit => {
        const foundUnitPosition = positions.find(({ unitId, userId }) =>
          userId
            ? invitedUsers.find(
                ({ userId: invitedId }) => userId === invitedId,
              ) || eventUserId === userId
            : +unit.unitId === +unitId,
        );

        return foundUnitPosition
          ? simpleUnitFormat(foundUnitPosition, positions)
          : simpleUnitFormat(unit, positions);
      });

      return {
        ...event,
        resource: {
          ...event.resource,
          validUnits: formattedUnits,
        },
      };
    }),
);

export const unitsFilterOptionsSel = createSelector(
  organizationPositionsSel,
  allSubUnitsSel,
  // These are for councils
  selectedUnitSel,
  unitInfoSel,
  //------------------
  (units, allSubUnits, councilSelectedUnit, currentUnitInfo) => {
    const isCouncilUsingUnit =
      councilSelectedUnit?.organizationGuid ===
      currentUnitInfo?.organizationGuid;
    /** @type {Record<string, import('@modules/calendar/duck/types').CalendarFiltersOptions>} */
    const formattedUnitsSubunits = {};

    let validUnits = _.uniqBy(
      units.filter(unit => unit.unitId),
      'unitId',
    );

    if (isCouncilUsingUnit && currentUnitInfo.sbOrganizationId) {
      validUnits = [
        {
          unitId: `${currentUnitInfo.sbOrganizationId}`,
          organizationName: councilSelectedUnit.organizationName,
          acceptGender: currentUnitInfo.acceptGender,
          program: councilSelectedUnit.program,
          isParentGuardian: false,
        },
      ];
    }

    validUnits.forEach(unit => {
      const {
        unitId,
        organizationName,
        acceptGender,
        program,
        isParentGuardian,
      } = unit;
      formattedUnitsSubunits[`${unit.unitId}-`] = {
        id: unitId,
        organizationName,
        acceptGender,
        program,
        isParentGuardian,
        active: true,
      };

      const foundSubUnits = allSubUnits.find(
        subUnits =>
          subUnits && subUnits.length && subUnits[0].unitId === +unit.unitId,
      );

      if (foundSubUnits) {
        const sortedFoundSubUnits = sortFoundSubUnits(foundSubUnits);

        sortedFoundSubUnits.forEach(subUnit => {
          const isDen = !!subUnit.denType;
          formattedUnitsSubunits[`${subUnit.subUnitId}-`] = {
            id: subUnit.subUnitId,
            organizationName: isDen
              ? `${subUnit.denType} ${subUnit.subUnitName}`
              : subUnit.subUnitName,
            isSubUnit: true,
            unitId: subUnit.unitId,
            active: true,
          };
        });
      }
    });

    return formattedUnitsSubunits;
  },
);

export const calendarFiltersSel = createSelector(
  unitsFilterOptionsSel,
  calendarsToHideSel,
  allUnitsWithColorsSel,
  (options, calendarsToHide, defaultValues) => {
    const filter = {};
    const calendarRecordById = _.keyBy(defaultValues, 'id');
    Object.keys(options).forEach(option => {
      const rawId = `${option}`.replace('-', '');
      // get the enabled/disabled setting from calendar record
      const calendarRecord = calendarRecordById[rawId];
      filter[option] = calendarRecord
        ? calendarRecord?.colorRecord?.showCalendar
        : !calendarsToHide.has(option);
    });

    return filter;
  },
);
export const defaultActiveCalendarsSel = createSelector(
  allUnitsWithColorsSel,
  items => {
    const calendarRecordById = _.chain(items || [])
      .map(item => [item.id, !!item.colorRecord?.showCalendar])
      .fromPairs()
      .value();
    return calendarRecordById;
  },
);

export const calendarKeysSel = createSelector(unitsFilterOptionsSel, keys =>
  Object.keys(keys),
);

export const filteredEventsSel = createSelector(
  eventsWithFormatedUnits,
  activityTypeFiltersSel,
  (events, activityFilters) =>
    events.filter(
      ({ resource: { activityTypeId } }) =>
        !activityTypeId || activityFilters[activityTypeId],
    ),
);

export const eventListSel = createSelector(
  filteredEventsSel,
  userIdSel,
  (filteredEvents, userId) =>
    filteredEvents.map(event => ({
      ...event,
      resource: {
        ...event.resource,
        rsvpAllowed: allowUserChangeRsvp({
          invitedUsers: event.resource.invitedUsers,
          userId,
          canRsvp: event.resource.canRsvp,
        }),
      },
    })),
);

export const eventsDuplicatedByUnitSel = createSelector(
  eventListSel,
  calendarRecordsSel,
  allUnitsSubunitsSel,
  calendarFiltersSel,
  (events, colorRecords, userUnitsSubunits, filters) => {
    const duplicatedEvents = [];
    events.forEach(event => {
      const { resource } = event;
      resource.units.forEach(unit => {
        const foundUnit = userUnitsSubunits.find(userUnit => {
          const eventUnitId = unit.patrolId || unit.denId || unit.unitId;
          return filters[`${eventUnitId}-`] && eventUnitId === userUnit.id;
        });

        if (!foundUnit) {
          return;
        }

        const foundRecord = colorRecords.find(record => {
          const colorUnitId = record.patrolId || record.denId || record.unitId;
          const eventUnitId = unit.patrolId || unit.denId || unit.unitId;

          return eventUnitId === colorUnitId;
        });

        const resultColor = foundRecord ? foundRecord.color : '#c2c0c0';

        const clonedEvent = _.cloneDeep(event);
        clonedEvent.color = resultColor;
        clonedEvent.resource.primaryUnit = { ...unit, color: resultColor };
        duplicatedEvents.push(clonedEvent);
      });
    });

    return duplicatedEvents;
  },
);

export const calendarItemsSel = createSelector(
  eventsDuplicatedByUnitSel,
  events =>
    events.sort(
      sorters.compose(
        sorters.date('resource.startDate'),
        sorters.stringMulti('resource.activityType', 'title'),
      ),
    ),
);

export const isCalendarRouteSel = state =>
  isCurrentPageSel(state, ROUTE_CALENDAR);

export const isEventRouteSel = state =>
  isCurrentPageSel(state, ROUTE_CALENDAR_EVENT_DETAILS);

export const isActivityEventRouteSel = state =>
  isCurrentPageSel(state, ROUTE_CALENDAR_ACTIVITY_DETAILS);

export const viewCalendarUnitSel = createSelector(
  calendarViewSel,
  calendarView =>
    calendarView === calendarViews.WEEK
      ? calendarViews.WEEK
      : calendarViews.MONTH,
);

export const deleteRecurrentEventInfoSel = createSelector(moduleSel, state => {
  /** @type {import('@modules/events/duck/types').DeleteRecurrentEventState} */
  const info = state.deleteRecurrentEventInfo;
  return info;
});

export const youthDenChiefOrgsSel = createSelector(
  getRelatedChildrenSel,
  userDataSel,
  (relatedChildren, userData) => {
    let youthOrgPositions = [];
    const isAdult = isValueTruthy(userData.profile.isAdult);

    // User is Parents with children
    if (isAdult && relatedChildren.length) {
      youthOrgPositions = relatedChildren.reduce(
        (acc, child) =>
          child?.organizationPositions?.length
            ? [...acc, ...child.organizationPositions]
            : [...acc],
        [],
      );
      const denChiefOrgs = youthOrgPositions
        .filter(({ positions }) =>
          positions.some(pos => +pos.id === PositionId.DenChief),
        )
        .map(({ organizationGuid }) => organizationGuid);
      return uniq(denChiefOrgs);
    }

    // User is Youth
    if (!isAdult && userData.organizations?.length) {
      youthOrgPositions = [...userData.organizations];
      const denChiefOrgs = youthOrgPositions
        .filter(({ allRoles }) => allRoles.includes('Den Chief'))
        .map(({ organizationGuid }) => organizationGuid);
      return uniq(denChiefOrgs);
    }

    return [];
  },
);

export const youthDenChiefMIDSel = createSelector(
  getRelatedChildrenSel,
  userDataSel,
  (relatedChildren, userData) => {
    let MIDs = [];
    const isAdult = isValueTruthy(userData.profile.isAdult);

    // User is Parent with children
    if (isAdult && relatedChildren.length) {
      MIDs = relatedChildren
        .filter(
          child =>
            child.memberId &&
            child.organizationPositions?.find(({ positions }) =>
              positions.find(pos => +pos.id === PositionId.DenChief),
            ),
        )
        .map(child => child.memberId);
      return uniq(MIDs);
    }

    // User is Youth
    if (!isAdult && userData.profile?.memberId) {
      return [userData.profile.memberId];
    }

    return [];
  },
);
